import { useState } from "react";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPanel,
  AlertDialogPopup,
  AlertDialogTitle,
  Box,
  Button,
  Skeleton,
  Text,
} from "@salestrak/ui";

import type { WorkspaceDeletionImpactRow } from "#app/lib/data";
import type { Workspace } from "#app/lib/data/workspace-context";
import {
  WORKSPACE_DELETION_GRACE_DAYS,
  useWorkspaceDeletion,
} from "#app/lib/hooks/use-workspace-deletion";
import { useWorkspaceDeletionImpact } from "#app/lib/hooks/use-workspace-deletion-impact";
import {
  TypedConfirmationField,
  confirmationMatches,
} from "#app/components/settings/typed-confirmation-field";

// What the impact block lists, in reading order. Every row is shown — a zero
// says "nothing here" in a way an omitted row cannot.
const IMPACT_ROWS: ReadonlyArray<{
  key: keyof WorkspaceDeletionImpactRow;
  singular: string;
  plural: string;
}> = [
  { key: "members", singular: "Member", plural: "Members" },
  { key: "projects", singular: "Project", plural: "Projects" },
  { key: "workflows", singular: "Workflow", plural: "Workflows" },
  { key: "teams", singular: "Team", plural: "Teams" },
  { key: "customFields", singular: "Custom field", plural: "Custom fields" },
  { key: "automations", singular: "Automation", plural: "Automations" },
  { key: "integrations", singular: "Integration", plural: "Integrations" },
  { key: "webhooks", singular: "Webhook", plural: "Webhooks" },
  { key: "apiKeys", singular: "API key", plural: "API keys" },
  { key: "mediaFiles", singular: "Photo or file", plural: "Photos and files" },
];

function ImpactList({ workspaceId }: { workspaceId: string }) {
  const { impact, initialLoading } = useWorkspaceDeletionImpact(workspaceId);

  return (
    <Box
      as="ul"
      columns={2}
      gapX="md"
      gapY="xs"
      paddingX="md"
      paddingY="sm"
      rounded="lg"
      background="sunken"
    >
      {IMPACT_ROWS.map((row) => (
        <Box as="li" key={row.key} direction="row" align="baseline" gap="xs">
          {initialLoading || !impact ? (
            // Ten COUNT(*)s over a big workspace can take a beat; a small
            // one answers before the 200ms gate and never shows a bar.
            <Skeleton delayed height={14} width={24} />
          ) : (
            <Text size="sm" weight="medium">
              {impact[row.key].toLocaleString()}
            </Text>
          )}
          <Text size="sm" variant="muted">
            {impact && impact[row.key] === 1 ? row.singular : row.plural}
          </Text>
        </Box>
      ))}
    </Box>
  );
}

/**
 * THE workspace-deletion confirmation, opened from the settings danger zone.
 * Three checks with three jobs: the live impact list informs, the typed
 * workspace name proves intent, and the destructive button stays disabled
 * until the name matches. What it schedules is reversible for 30 days — there
 * is no immediate-delete variant. Renders open; the host unmounts it when
 * `onOpenChange(false)` fires.
 */
export function DeleteWorkspaceDialog({
  workspace,
  onOpenChange,
}: {
  workspace: Pick<Workspace, "workspaceId" | "name">;
  onOpenChange: (open: boolean) => void;
}) {
  const [typed, setTyped] = useState("");
  const { scheduleDeletion, busy } = useWorkspaceDeletion(workspace.workspaceId);
  const matches = confirmationMatches(typed, workspace.name);

  const title = `Delete ${workspace.name}?`;
  const description = `The workspace is frozen immediately — nobody can open it — and permanently deleted after ${WORKSPACE_DELETION_GRACE_DAYS} days. Any Owner can restore it until then. After that, everything below is gone for good.`;
  const confirmLabel = "Schedule deletion";

  async function handleConfirm() {
    if (await scheduleDeletion(typed)) onOpenChange(false);
  }

  return (
    <AlertDialog open onOpenChange={onOpenChange}>
      {/* Type-to-confirm — a stray outside click must not eat the typed name. */}
      <AlertDialogPopup disablePointerDismissal>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogPanel>
          <Box gap="md">
            <ImpactList workspaceId={workspace.workspaceId} />
            <TypedConfirmationField
              disabled={busy}
              name={workspace.name}
              noun="workspace"
              onChange={setTyped}
              onSubmit={() => void handleConfirm()}
              value={typed}
            />
          </Box>
        </AlertDialogPanel>

        <AlertDialogFooter>
          <AlertDialogClose disabled={busy} render={<Button variant="outline" />}>
            Cancel
          </AlertDialogClose>
          <Button
            disabled={!matches || busy}
            loading={busy}
            onClick={() => void handleConfirm()}
            color="destructive"
          >
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
